import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { useNavigate } from 'react-router-dom';
import { User, Mail, Phone, MapPin, Calendar, Edit2, Save, X, Camera, BookOpen, Users, Award } from 'lucide-react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import {api} from '../../api';

interface ProfileForm {
  name: string;
  email: string;
  phone: string;
  bio: string;
  location: string;
}

const ProfessorProfile: React.FC = () => {
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [profileImage, setProfileImage] = useState<string>('');
  const [userData, setUserData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  
  const { register, handleSubmit, reset, setValue, formState: { errors } } = useForm<ProfileForm>();

  useEffect(() => {
    loadUserData();
  }, []);

  const loadUserData = async () => {
    try {
      setLoading(true);
      const response = await api.get('/auth/me');

      if (response.data) {
        const data = response.data;
        setUserData(data.user);
        
        // Set form values
        setValue('name', data.user.name);
        setValue('email', data.user.email);
        setValue('phone', data.user.phone || '');
        setValue('bio', data.user.bio || '');
        
        // Set avatar if exists
        if (data.user.avatar) {
          setProfileImage(data.user.avatar);
        }
      } else {
        toast.error('Erreur lors du chargement des données utilisateur');
      }
    } catch (error) {
      console.error('Load user data error:', error);
      toast.error('Erreur lors du chargement des données utilisateur');
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data: ProfileForm) => {
    try {
      const formData = new FormData();
      
      // Add form fields
      Object.entries(data).forEach(([key, value]) => {
        formData.append(key, value);
      });
      
      // Add profile image if changed
      if (profileImage && profileImage.startsWith('data:')) {
        // Convert base64 to file
        const response = await fetch(profileImage);
        const blob = await response.blob();
        formData.append('avatar', blob, 'avatar.jpg');
      }
      
      const response = await api.put(`/users/${userData._id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.data) {
        toast.success('Profil mis à jour avec succès');
        setIsEditing(false);
        loadUserData();
      } else {
        toast.error('Erreur lors de la mise à jour du profil');
      }
    } catch (error) {
      console.error('Update profile error:', error);
      toast.error('Erreur lors de la mise à jour du profil');
    }
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setProfileImage(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    reset();
    
    // Reset profile image
    if (userData?.avatar) {
      setProfileImage(userData.avatar);
    } else {
      setProfileImage('');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold text-gray-900">Mon Profil</h1>
          {!isEditing ? (
            <button
              onClick={() => setIsEditing(true)}
              className="flex items-center px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors"
            >
              <Edit2 className="h-4 w-4 mr-2" />
              Modifier le profil
            </button>
          ) : (
            <div className="flex space-x-2">
              <button
                onClick={handleSubmit(onSubmit)}
                className="flex items-center px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors"
              >
                <Save className="h-4 w-4 mr-2" />
                Enregistrer
              </button>
              <button
                onClick={handleCancel}
                className="flex items-center px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
              >
                <X className="h-4 w-4 mr-2" />
                Annuler
              </button>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Profile Information */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Info */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-6">Informations personnelles</h2>
              
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                <div className="flex items-center space-x-6">
                  <div className="relative">
                    <div className="w-24 h-24 bg-brand-600 rounded-full flex items-center justify-center text-white text-2xl font-bold overflow-hidden">
                      {profileImage ? (
                        <img src={profileImage} alt="Profile" className="w-full h-full object-cover" />
                      ) : (
                        userData?.name?.charAt(0).toUpperCase() || 'P'
                      )}
                    </div>
                    {isEditing && (
                      <label className="absolute bottom-0 right-0 bg-white rounded-full p-2 shadow-lg cursor-pointer hover:bg-gray-50">
                        <Camera className="h-4 w-4 text-gray-600" />
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleImageUpload}
                          className="hidden"
                        />
                      </label>
                    )}
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">{userData?.name}</h3>
                    <p className="text-gray-600">Professeur</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <User className="inline h-4 w-4 mr-1" />
                      Nom complet
                    </label>
                    <input
                      {...register('name', { required: 'Le nom est requis' })}
                      disabled={!isEditing}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 disabled:bg-gray-50"
                    />
                    {errors.name && (
                      <p className="mt-1 text-sm text-brand-600">{errors.name.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Mail className="inline h-4 w-4 mr-1" />
                      Email
                    </label>
                    <input
                      {...register('email', { 
                        required: 'L\'email est requis',
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                          message: 'Email invalide'
                        }
                      })}
                      disabled={!isEditing}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 disabled:bg-gray-50"
                    />
                    {errors.email && (
                      <p className="mt-1 text-sm text-brand-600">{errors.email.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Phone className="inline h-4 w-4 mr-1" />
                      Téléphone
                    </label>
                    <input
                      {...register('phone')}
                      disabled={!isEditing}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 disabled:bg-gray-50"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <MapPin className="inline h-4 w-4 mr-1" />
                      Localisation
                    </label>
                    <input
                      {...register('location')}
                      disabled={!isEditing}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 disabled:bg-gray-50"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Biographie
                  </label>
                  <textarea
                    {...register('bio')}
                    disabled={!isEditing}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 disabled:bg-gray-50"
                  />
                </div>
              </form>
            </div>

            {/* Courses */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-6">Mes cours</h2>
              
              {userData?.createdCourses?.length > 0 ? (
                <div className="space-y-4">
                  {userData.createdCourses.map((course: any) => (
                    <div key={course._id} className="flex items-center p-4 bg-gray-50 rounded-lg">
                      <img
                        src={course.thumbnail || 'https://images.pexels.com/photos/274422/pexels-photo-274422.jpeg'}
                        alt={course.title}
                        className="w-16 h-16 rounded-lg object-cover mr-4"
                      />
                      <div className="flex-1">
                        <h3 className="font-medium text-gray-900">{course.title}</h3>
                        <div className="flex items-center text-sm text-gray-500 mt-1">
                          <Users className="h-4 w-4 mr-1" />
                          <span>{course.enrolledStudents?.length || 0} étudiants</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <BookOpen className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun cours créé</h3>
                  <p className="text-gray-500 mb-4">
                    Vous n'avez pas encore créé de cours
                  </p>
                  <button
                    onClick={() => navigate('/professor/courses/create')}
                    className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors"
                  >
                    Créer un cours
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Stats Sidebar */}
          <div className="space-y-6">
            {/* Quick Stats */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Statistiques</h2>
              <div className="space-y-4">
                <div className="flex justify-between">
                  <span className="text-gray-600">Cours créés</span>
                  <span className="font-semibold">{userData?.createdCourses?.length || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Étudiants totaux</span>
                  <span className="font-semibold">{userData?.totalStudents || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Quiz créés</span>
                  <span className="font-semibold">{userData?.totalQuizzes || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Note moyenne</span>
                  <span className="font-semibold">{userData?.averageRating || 'N/A'}</span>
                </div>
              </div>
            </div>

            {/* Account Info */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Informations du compte</h2>
              <div className="space-y-3">
                <div className="flex items-center text-sm">
                  <Calendar className="h-4 w-4 text-gray-400 mr-2" />
                  <span className="text-gray-600">Membre depuis</span>
                  <span className="ml-auto font-medium">
                    {new Date(userData?.createdAt).toLocaleDateString('fr-FR', {
                      month: 'long',
                      year: 'numeric'
                    })}
                  </span>
                </div>
                <div className="flex items-center text-sm">
                  <User className="h-4 w-4 text-gray-400 mr-2" />
                  <span className="text-gray-600">Type de compte</span>
                  <span className="ml-auto font-medium">Professeur</span>
                </div>
                <div className="flex items-center text-sm">
                  <Calendar className="h-4 w-4 text-gray-400 mr-2" />
                  <span className="text-gray-600">Dernière connexion</span>
                  <span className="ml-auto font-medium">
                    {new Date(userData?.lastLogin).toLocaleDateString('fr-FR')}
                  </span>
                </div>
              </div>
            </div>

            {/* Security */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Sécurité</h2>
              <div className="space-y-3">
                <button 
                  className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                  onClick={() => toast.success('Fonctionnalité à venir')}
                >
                  Changer le mot de passe
                </button>
                <button 
                  className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                  onClick={() => toast.success('Fonctionnalité à venir')}
                >
                  Authentification à deux facteurs
                </button>
                <button 
                  className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                  onClick={() => toast.success('Fonctionnalité à venir')}
                >
                  Sessions actives
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ProfessorProfile;